import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Button } from 'reactstrap';
import { BiTrash, BiErrorCircle as BiAlertCircle } from 'react-icons/bi';
import debounce from 'lodash/debounce';
import { formatCurrency } from '../../utils/formatters';
import useCostTypes from '../../hooks/useCostTypes';

// Fallback if cost types haven't loaded yet.
const FALLBACK_COST_TYPES = ['Labour', 'Materials', 'Other'];

// Fields the row owns. LineTotal is derived (qty * unitPrice) and shown
// read-only; SortOrder is driven by the parent (add / drag later), never
// edited in the row itself.
const FIELDS = ['description', 'quantity', 'unitPrice', 'estimatedCost', 'costType'];

function toDraft(item) {
    return {
        description:   item?.description   ?? '',
        quantity:      item?.quantity      ?? 0,
        unitPrice:     item?.unitPrice     ?? 0,
        estimatedCost: item?.estimatedCost ?? 0,
        costType:      item?.costType      ?? 'Labour',
    };
}

function draftsEqual(a, b) {
    if (!a || !b) return a === b;
    return FIELDS.every(f => String(a[f] ?? '') === String(b[f] ?? ''));
}

// Debounced autosave mirroring customerDetailsCard — but per-row, with a
// 500ms debounce (a little snappier than the 600ms on the details card,
// since the expected edit cadence is "tab-tab-number" rather than prose).
export default function QuoteLineItemRow({ item, currency, onUpdate, onRemove, disabled, onDraftChange }) {
    const { data: costTypesData } = useCostTypes();
    const baseCostTypes = costTypesData?.length ? costTypesData.map(ct => ct.name) : FALLBACK_COST_TYPES;
    // Include the item's current value if it's not in the list (legacy data).
    const costTypes = item?.costType && !baseCostTypes.includes(item.costType)
        ? [...baseCostTypes, item.costType]
        : baseCostTypes;
    const [draft, setDraft] = useState(() => toDraft(item));
    const lastSavedRef = useRef(toDraft(item));
    const draftRef = useRef(draft);
    useEffect(() => { draftRef.current = draft; });

    // Re-seed when the item id changes OR when the server echoes back a
    // fresh LineTotal (qty/unitPrice recompute) that differs from ours.
    useEffect(() => {
        const fresh = toDraft(item);
        if (!draftsEqual(fresh, lastSavedRef.current)) {
            setDraft(fresh);
            lastSavedRef.current = fresh;
        }
    }, [item]);

    const saveRef = useRef(null);
    useEffect(() => {
        saveRef.current = () => {
            if (disabled) return;
            const body = draftRef.current;
            if (draftsEqual(body, lastSavedRef.current)) return;
            onUpdate({
                id: item.id,
                body: {
                    description:   body.description,
                    quantity:      Number(body.quantity)      || 0,
                    unitPrice:     Number(body.unitPrice)     || 0,
                    estimatedCost: Number(body.estimatedCost) || 0,
                    costType:      body.costType,
                    sortOrder:     item.sortOrder,
                },
            });
            lastSavedRef.current = body;
        };
    });

    const debouncedSave = useMemo(
        () => debounce(() => saveRef.current?.(), 500),
        []
    );

    useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

    // Report initial draft on mount.
    useEffect(() => {
        onDraftChange?.(item.id, draft);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (field, value) => {
        setDraft(prev => {
            const next = { ...prev, [field]: value };
            onDraftChange?.(item.id, next);
            return next;
        });
        debouncedSave();
    };

    const handleBlur = () => {
        // Blur commits immediately — debouncing is for keystroke storms; the
        // user moving focus is a clear "I'm done with this cell" signal.
        debouncedSave.flush();
    };

    // Derived — shown live while typing, independent of server round-trip.
    const liveLineTotal = (Number(draft.quantity) || 0) * (Number(draft.unitPrice) || 0);
    const isIncomplete = !draft.description?.trim() || !(Number(draft.quantity) > 0);

    return (
        <tr style={isIncomplete ? { background: '#fef2f2' } : undefined}>
            <td style={{ minWidth: 200 }}>
                <div className="d-flex align-items-center gap-1">
                    {isIncomplete && <BiAlertCircle className="text-danger flex-shrink-0" />}
                    <Input bsSize="sm"
                        value={draft.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        onBlur={handleBlur}
                        disabled={disabled}
                        className={!draft.description?.trim() ? 'border-danger' : ''}
                    />
                </div>
            </td>
            <td style={{ width: 90 }}>
                <Input bsSize="sm" type="number" step="0.01"
                    value={draft.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    onBlur={handleBlur}
                    disabled={disabled}
                    className={!(Number(draft.quantity) > 0) ? 'border-danger' : ''}
                />
            </td>
            <td style={{ width: 120 }}>
                <Input bsSize="sm" type="number" step="0.01"
                    value={draft.unitPrice}
                    onChange={(e) => handleChange('unitPrice', e.target.value)}
                    onBlur={handleBlur}
                    disabled={disabled}
                />
            </td>
            <td style={{ width: 120 }}>
                <Input bsSize="sm" type="number" step="0.01"
                    value={draft.estimatedCost}
                    onChange={(e) => handleChange('estimatedCost', e.target.value)}
                    onBlur={handleBlur}
                    disabled={disabled}
                />
            </td>
            <td style={{ width: 130 }}>
                <Input bsSize="sm" type="select"
                    value={draft.costType}
                    onChange={(e) => handleChange('costType', e.target.value)}
                    onBlur={handleBlur}
                    disabled={disabled}
                >
                    {costTypes.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </Input>
            </td>
            <td className="text-end" style={{ width: 120 }}>
                {formatCurrency(liveLineTotal, currency)}
            </td>
            <td style={{ width: 50 }}>
                <Button color="link" size="sm" className="p-1 text-danger"
                        onClick={() => onRemove(item.id)}
                        disabled={disabled}
                        aria-label="Delete line item">
                    <BiTrash />
                </Button>
            </td>
        </tr>
    );
}
